import { useCallback, useEffect, useState } from 'react';
import { Loader2, ScrollText, CircleCheck, CircleX, CircleMinus, Clock } from 'lucide-react';
import { useCompany } from '@/context/CompanyContext';
import { DIRECTION_LABELS, FLOWS, flowByKey } from '@/lib/interfacesCatalog';
import { interfacesService, type ActivityLogEntry } from '@/lib/interfacesService';

const STATUS_META: Record<ActivityLogEntry['status'], { label: string; className: string; icon: React.ReactNode }> = {
  success: { label: 'הצליח', className: 'bg-emerald-50 text-emerald-700', icon: <CircleCheck className="h-3.5 w-3.5" /> },
  failed: { label: 'נכשל', className: 'bg-red-50 text-red-700', icon: <CircleX className="h-3.5 w-3.5" /> },
  skipped: { label: 'דולג', className: 'bg-gray-100 text-gray-600', icon: <CircleMinus className="h-3.5 w-3.5" /> },
  pending: { label: 'ממתין', className: 'bg-amber-50 text-amber-700', icon: <Clock className="h-3.5 w-3.5" /> },
};

const selectClass =
  'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200';

export function InterfacesActivityLogPage() {
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const [entries, setEntries] = useState<ActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<ActivityLogEntry['status'] | ''>('');
  const [flowKey, setFlowKey] = useState('');

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      setEntries(await interfacesService.listActivity(companyId, {
        status: status || undefined,
        flowKey: flowKey || undefined,
      }));
    } catch (e) {
      console.error('Failed to load interface activity log:', e);
      setError('לא הצלחנו לטעון את יומן הפעולות');
    } finally {
      setLoading(false);
    }
  }, [companyId, status, flowKey]);

  useEffect(() => { load(); }, [load]);

  const filtered = !!status || !!flowKey;

  return (
    <div className="mx-auto max-w-6xl space-y-6" dir="rtl">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">יומן פעולות</h1>
          <p className="mt-1 text-gray-600">כל העברה בין המערכות מתועדת כאן, כולל הצלחות וכשלונות.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={flowKey} onChange={e => setFlowKey(e.target.value)} className={selectClass} aria-label="סינון לפי ממשק">
            <option value="">כל הממשקים</option>
            {FLOWS.filter(f => f.status === 'available').map(f => (
              <option key={f.key} value={f.key}>{f.title}</option>
            ))}
          </select>
          <select
            value={status}
            onChange={e => setStatus(e.target.value as ActivityLogEntry['status'] | '')}
            className={selectClass}
            aria-label="סינון לפי סטטוס"
          >
            <option value="">כל הסטטוסים</option>
            {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
          </select>
        </div>
      </header>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-sky-600" /></div>
      ) : error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-700">{error}</div>
      ) : entries.length === 0 ? (
        <div className="flex flex-col items-center rounded-2xl border border-gray-200 bg-white px-6 py-16 text-center shadow-sm">
          <div className="rounded-full bg-gray-100 p-4 text-gray-400"><ScrollText className="h-8 w-8" /></div>
          <p className="mt-4 font-semibold text-gray-900">{filtered ? 'אין פעולות שמתאימות לסינון' : 'עוד לא בוצעו העברות'}</p>
          <p className="mt-1 text-sm text-gray-500">
            {filtered ? 'נסו לשנות את הסינון.' : 'כשהממשקים יתחילו לפעול, כל העברה תופיע כאן.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr className="text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3">מועד</th>
                <th className="px-4 py-3">ממשק</th>
                <th className="px-4 py-3">כיוון</th>
                <th className="px-4 py-3">סטטוס</th>
                <th className="px-4 py-3">מקור ← יעד</th>
                <th className="px-4 py-3">פרטים</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {entries.map(e => {
                const meta = STATUS_META[e.status];
                return (
                  <tr key={e.id} className="transition-colors hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3 text-gray-600">
                      {new Date(e.created_at).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">{flowByKey(e.flow_key)?.title ?? e.flow_key}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-600">{DIRECTION_LABELS[e.direction]}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${meta.className}`}>
                        {meta.icon} {meta.label}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                      {e.source_ref ?? '—'} ← {e.target_ref ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      {e.error_message
                        ? <span className="text-red-700">{e.error_message}</span>
                        : <span className="text-gray-600">{e.summary ?? ''}</span>}
                      {e.attempts > 1 && <span className="mr-2 text-xs text-gray-400">({e.attempts} ניסיונות)</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
